import React, { useEffect } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DescriptionIcon from "@mui/icons-material/Description";
import ArticleIcon from "@mui/icons-material/Article";
import CalculateIcon from "@mui/icons-material/Calculate";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import { wbsLabel, isPvWbs } from "../../../../../../components/Drawers/TreeViewDrawer/useEmsProjectsTreeData";
import SharePointFolderBrowser from "./SharePointFolderBrowser";

// Converts a lazily-loaded branch's state ("loading" | array | undefined)
// into what StatTile needs to render: a count, or "loading".
const toCount = (state) =>
  Array.isArray(state) ? state.length : "loading";

// Small "N Documents" / spinner tile used across both the Plant and WBS
// summaries so the two read consistently. `count` is a number or "loading".
const StatTile = ({ icon, label, count }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      minWidth: 140,
    }}
  >
    {icon}
    <Box>
      <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
        {count === "loading" ? <CircularProgress size={12} /> : count}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Paper>
);

const PlantSummary = ({ plant, treeData, onSelectWbs, onOpenFile }) => {
  const { wbsByPlant, loadWbsForPlant } = treeData;
  const wbsList = wbsByPlant[plant.id];

  useEffect(() => {
    loadWbsForPlant(plant.id);
  }, [plant.id, loadWbsForPlant]);

  return (
    <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
      <Typography variant="overline" color="text.secondary">
        Plant
      </Typography>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {plant.name || "Untitled"}
      </Typography>

      <StatTile
        icon={<AccountTreeIcon color="primary" />}
        label="WBS / Projects"
        count={toCount(wbsList)}
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        WBS / Projects
      </Typography>
      {wbsList === "loading" || wbsList === undefined ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : wbsList.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No WBS under this plant.
        </Typography>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {wbsList.map((wbs) => (
              <ListItemButton
                key={wbs.id}
                onClick={() => onSelectWbs(plant, wbs)}
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-of-type": { borderBottom: "none" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AccountTreeIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={wbsLabel(wbs)} />
                {isPvWbs(wbs) && <Chip label="PV" size="small" />}
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        SharePoint
      </Typography>
      <SharePointFolderBrowser plantName={plant.name} onOpenFile={onOpenFile} />
    </Box>
  );
};

const WbsSummary = ({ plant, wbs, treeData, onOpenFile }) => {
  const {
    docsByWbs,
    pvInputsByWbs,
    drawingsByWbs,
    loadDocsForWbs,
    loadDrawingsForWbs,
    loadPvInputsForWbs,
  } = treeData;
  const pv = isPvWbs(wbs);

  useEffect(() => {
    loadDocsForWbs(wbs.id);
    loadDrawingsForWbs(wbs.id);
    if (pv) loadPvInputsForWbs(wbs.id);
  }, [wbs.id, pv, loadDocsForWbs, loadDrawingsForWbs, loadPvInputsForWbs]);

  const pvInputs = pvInputsByWbs[wbs.id];
  const estimationCount = Array.isArray(pvInputs)
    ? pvInputs.filter((entry) => entry.estimationDocumentUID).length
    : "loading";

  return (
    <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
      <Typography variant="overline" color="text.secondary">
        {plant.name}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5">{wbsLabel(wbs)}</Typography>
        {pv && <Chip label="PV" size="small" />}
      </Stack>

      <Grid container spacing={2}>
        <Grid item>
          <StatTile
            icon={<DescriptionIcon color="disabled" />}
            label="Documents"
            count={toCount(docsByWbs[wbs.id])}
          />
        </Grid>
        {pv && (
          <>
            <Grid item>
              <StatTile
                icon={<ArticleIcon color="warning" />}
                label="Input Data revisions"
                count={toCount(pvInputs)}
              />
            </Grid>
            <Grid item>
              <StatTile
                icon={<CalculateIcon color="warning" />}
                label="Estimation revisions"
                count={estimationCount}
              />
            </Grid>
          </>
        )}
        <Grid item>
          <StatTile
            icon={<ArchitectureIcon color="warning" />}
            label="2D Drawings"
            count={toCount(drawingsByWbs[wbs.id])}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        SharePoint
      </Typography>
      <SharePointFolderBrowser
        plantName={plant.name}
        projectName={wbsLabel(wbs)}
        onOpenFile={onOpenFile}
      />
    </Box>
  );
};

/**
 * Right-pane overview shown by EmsExplorer when a Plant or WBS node is
 * selected (rather than a document, which gets DocumentFilesPanel, or a
 * file, which gets FilePreviewPanel). Reads counts from the same
 * lazily-loaded tree data the left-hand tree uses, triggering a load if a
 * branch hasn't been fetched yet.
 */
export default function SummaryPanel({ selection, treeData, onSelectWbs, onOpenFile }) {
  if (!selection) return null;
  if (selection.kind === "plant") {
    return (
      <PlantSummary
        plant={selection.plant}
        treeData={treeData}
        onSelectWbs={onSelectWbs}
        onOpenFile={onOpenFile}
      />
    );
  }
  if (selection.kind === "wbs") {
    return (
      <WbsSummary
        plant={selection.plant}
        wbs={selection.wbs}
        treeData={treeData}
        onOpenFile={onOpenFile}
      />
    );
  }
  return null;
}
